import React from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, Waves, Compass } from 'lucide-react'
import Container from './Container'
import WaveDivider from './WaveDivider'

const WhyPuntaCana: React.FC = () => {
  return (
    <section className="bg-background-alt py-24 md:py-32 relative">
      <WaveDivider position="top" color="#FFFFFF" />

      <Container size="lg" className="relative z-10">
        <div className="mb-16 text-center max-w-2xl mx-auto">
          <span className="mb-4 inline-block font-medium text-accent uppercase tracking-widest text-sm">
            Contexto del Destino
          </span>
          <h2 className="font-display text-3xl font-bold text-primary md:text-4xl lg:text-5xl mb-6">
            Punta Cana: destino global, vida real
          </h2>
          <p className="text-lg text-text-secondary">
            Punta Cana combina estilo de vida, conectividad, servicios y un mercado residencial en expansión. Por eso es uno de los destinos más buscados del Caribe para vivir, vacacionar o diversificar patrimonio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="md:col-span-12 lg:col-span-8 group relative overflow-hidden rounded-[2rem] bg-white p-8 md:p-12 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] border border-border-light transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] hover:-translate-y-1"
          >
            <div className="absolute top-0 right-0 -mr-20 -mt-20 h-64 w-64 rounded-full bg-accent/5 blur-3xl transition-all duration-700 group-hover:bg-accent/10"></div>
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent text-white shadow-lg shadow-accent/30">
                <Compass className="h-8 w-8" />
              </div>
              <div>
                <h3 className="mb-4 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">Destino Internacional Top</h3>
                <p className="text-text-secondary text-lg leading-relaxed max-w-xl">
                  Con demanda constante, vuelos directos desde múltiples ciudades de España (Madrid, Barcelona) y un atractivo natural inigualable que garantiza la ocupación turística.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="md:col-span-6 lg:col-span-4 group relative overflow-hidden rounded-[2rem] bg-primary p-8 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 text-white"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-light to-primary opacity-50"></div>
            <div className="absolute bottom-0 left-0 -ml-10 -mb-10 h-40 w-40 rounded-full bg-accent/20 blur-2xl transition-all duration-700 group-hover:scale-150"></div>
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-accent-light backdrop-blur-md">
                <TrendingUp className="h-7 w-7" />
              </div>
              <div>
                <h3 className="mb-3 font-display text-xl md:text-2xl font-bold tracking-tight">Crecimiento Sostenido</h3>
                <p className="text-secondary-light/80 leading-relaxed">
                  Infraestructura en expansión y servicios de primer nivel (hospitales, colegios) que aseguran plusvalía anual.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="md:col-span-6 lg:col-span-12 group relative overflow-hidden rounded-[2rem] bg-white p-8 md:p-10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] border border-border-light transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] hover:-translate-y-1 flex flex-col md:flex-row items-center gap-8"
          >
            <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-background-alt/50 blur-3xl transition-all duration-700 group-hover:bg-accent/5"></div>
            
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-3xl bg-background-alt text-primary shadow-inner border border-white relative z-10">
              <Waves className="h-10 w-10" />
            </div>
            
            <div className="relative z-10">
              <h3 className="mb-3 font-display text-2xl font-bold text-primary tracking-tight text-center md:text-left">Versatilidad Total</h3>
              <p className="text-text-secondary text-lg leading-relaxed text-center md:text-left max-w-4xl">
                Nuestros proyectos son ideales tanto para uso personal vacacional como para generar ingresos pasivos mediante opciones de administración hotelera. Tú decides cómo y cuándo usarlo.
              </p>
            </div>
          </motion.div>
        </div>
      </Container>
      
      <WaveDivider color="#0A2540" /> {/* Prepares background for next section if dark */}
    </section>
  )
}

export default WhyPuntaCana
